import {
  InformationCircleIcon,
  ArrowLeftIcon,
} from "@heroicons/react/24/outline";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageMainCard, PageMainStack } from "@/core/components/page-components";
import { Button } from "@/core/components/ui/button";
import { Card } from "@/core/components/ui/card";
import { ScrollArea } from "@/core/components/ui/scroll-area";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { useGetBusinessReviewAccess } from "@/business/query";
import { PageHeader } from "@/business-settings/common/page-header";
import { useBusiness } from "@/core/business";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { cn } from "@/core/utils/utils";
import { SPIFF_RULE_EVENTS } from "@/spiff-rules/constants";
import {
  useGetActiveSpiffRules,
  useGetLatestSpiffRules,
} from "@/spiff-rules/query";
import { SpiffRule } from "@/spiff-rules/spiff-rule";
import { isShownSpiffRule, sortSpiffRulesByOrder } from "@/spiff-rules/utils";
import { useGetMyUser } from "@/user/query";
import { isAdminOrOwnerOrManager } from "@/user/utils";
import { SpiffRuleForm } from "./spiff-rule-form/spiff-rule-form";
import { SpiffRuleListItem } from "./spiff-rule-list-item";

const SpiffPrograms = () => {
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();

  const { businessId, isSpiffOnly } = useBusiness();
  const { data: userInfo, isLoading: isUserInfoLoading } = useGetMyUser();

  const { enabled: yardSignPhotoSpiffEnabled } = useFeatureFlag(
    FEATURE_FLAG_NAMES.yard_sign_photo_spiff,
  );

  const canControlSpiffPrograms =
    !isUserInfoLoading && isAdminOrOwnerOrManager(userInfo?.role);

  const { data: reviewAccessData } = useGetBusinessReviewAccess(businessId);

  const { data: latestSpiffRules = [] } = useGetLatestSpiffRules(businessId);
  const { data: activeSpiffRules = [] } = useGetActiveSpiffRules(businessId);

  const sortedActiveSpiffRules = useMemo(
    () =>
      activeSpiffRules
        .filter(isShownSpiffRule)
        .sort((a, b) => sortSpiffRulesByOrder(a.event, b.event)),
    [activeSpiffRules],
  );

  const signupRule = latestSpiffRules.find(
    (rule: SpiffRule) => rule.event === SPIFF_RULE_EVENTS.signup,
  );
  const referralCreationRule = latestSpiffRules.find(
    (rule: SpiffRule) => rule.event === SPIFF_RULE_EVENTS.referral_creation,
  );
  const fiveStarReviewRule = latestSpiffRules.find(
    (rule: SpiffRule) => rule.event === SPIFF_RULE_EVENTS.five_star_review,
  );
  const yardSignPhotoRule = latestSpiffRules.find(
    (rule: SpiffRule) => rule.event === SPIFF_RULE_EVENTS.yard_sign_photo,
  );

  const [signupFormDirty, setSignupFormDirty] = useState(false);
  const [referralCreationFormDirty, setReferralCreationFormDirty] =
    useState(false);
  const [fiveStarReviewFormDirty, setFiveStarReviewFormDirty] = useState(false);
  const [yardSignPhotoFormDirty, setYardSignPhotoFormDirty] = useState(false);

  const anyFormsDirty =
    signupFormDirty ||
    referralCreationFormDirty ||
    fiveStarReviewFormDirty ||
    yardSignPhotoFormDirty;

  const hasActiveRules = sortedActiveSpiffRules.length > 0;

  return (
    <div className="mx-auto w-full max-w-[1600px] px-6 pt-8">
      <Stack
        gap="md"
        fullWidth
        className={cn(hasActiveRules && "items-center 2xl:items-stretch")}
      >
        <Stack
          gap="md"
          className={cn(
            "flex-col 2xl:flex-row",
            hasActiveRules && "items-center 2xl:items-start",
          )}
        >
          {/* Forms Section */}
          <Stack
            gap="md"
            className={cn(
              "w-full max-w-4xl flex-1 2xl:mx-0 2xl:max-w-none",
              hasActiveRules && "mx-auto",
              sortedActiveSpiffRules.length > 0 &&
                !isEditing &&
                "hidden 2xl:flex",
            )}
          >
            <Stack direction="row" align="center" justify="between" fullWidth>
              <PageHeader>Spiff Programs</PageHeader>
              {sortedActiveSpiffRules.length > 0 && isEditing && (
                <Button
                  variant="secondary"
                  onClick={() => setIsEditing(false)}
                  className="2xl:hidden"
                  size="sm"
                >
                  View Active
                </Button>
              )}
            </Stack>

            <button
              onClick={() => navigate("/spiffs")}
              className="flex w-fit items-center gap-1 text-sm text-primary underline hover:text-primary/80"
            >
              <ArrowLeftIcon className="h-3 w-3" />
              Back to Spiff Payroll
            </button>

            {anyFormsDirty && (
              <Card className="flex items-center gap-2 border bg-blue-5 px-3 py-2">
                <InformationCircleIcon className="size-6 flex-shrink-0" />
                <Typography>
                  You have unsaved changes on this page. Remember to save your
                  changes for each spiff program that you edit.
                </Typography>
              </Card>
            )}

            {!isSpiffOnly && (
              <Card className="h-auto w-full overflow-visible">
                <Stack className="p-6">
                  <SpiffRuleForm
                    businessId={businessId}
                    spiffRule={signupRule}
                    event={SPIFF_RULE_EVENTS.signup}
                    title="Sign-up payments"
                    description="Spiff payments to technicians for each new sign-up to the referral program."
                    setFormDirty={setSignupFormDirty}
                    disabled={!canControlSpiffPrograms}
                    disabledTooltip="You must be an admin, owner, or manager to control spiff programs"
                  />
                </Stack>
              </Card>
            )}

            {!isSpiffOnly && (
              <Card className="h-auto w-full overflow-visible">
                <Stack className="p-6">
                  <SpiffRuleForm
                    businessId={businessId}
                    spiffRule={referralCreationRule}
                    event={SPIFF_RULE_EVENTS.referral_creation}
                    title="Referral payments"
                    description="Spiff payments to technicians for each new referral originated by them."
                    setFormDirty={setReferralCreationFormDirty}
                    disabled={!canControlSpiffPrograms}
                    disabledTooltip="You must be an admin, owner, or manager to control spiff programs"
                  />
                </Stack>
              </Card>
            )}

            <Card className="h-auto w-full overflow-visible">
              <Stack className="p-6">
                <SpiffRuleForm
                  businessId={businessId}
                  spiffRule={fiveStarReviewRule}
                  event={SPIFF_RULE_EVENTS.five_star_review}
                  title="Five star review payments"
                  description="Spiff payments to technicians for each five star review from a customer on a job they completed."
                  setFormDirty={setFiveStarReviewFormDirty}
                  disabled={
                    !reviewAccessData?.has_review_access ||
                    !canControlSpiffPrograms
                  }
                  disabledTooltip="You must be using Service Titan Marketing Pro or have connected your Google Business in order to enable review spiffing. You must also be an admin, owner, or manager to control spiff programs."
                />
              </Stack>
            </Card>

            {yardSignPhotoSpiffEnabled && (
              <Card className="h-auto w-full overflow-visible">
                <Stack className="p-6">
                  <SpiffRuleForm
                    businessId={businessId}
                    spiffRule={yardSignPhotoRule}
                    event={SPIFF_RULE_EVENTS.yard_sign_photo}
                    title="Yard sign photo payments"
                    description="Spiff payments to technicians for each yard sign photo submitted at a customer's location."
                    setFormDirty={setYardSignPhotoFormDirty}
                    disabled={!canControlSpiffPrograms}
                    disabledTooltip="You must be an admin, owner, or manager to control spiff programs"
                  />
                </Stack>
              </Card>
            )}
          </Stack>

          {/* Active Rules Sidebar */}
          {sortedActiveSpiffRules.length > 0 && (
            <Stack
              gap="md"
              className={cn(
                "w-full max-w-4xl 2xl:mx-0 2xl:w-[600px] 2xl:max-w-none 2xl:flex-shrink-0",
                hasActiveRules && "mx-auto",
                isEditing && "hidden 2xl:flex",
              )}
            >
              <Stack direction="row" align="center" justify="between" fullWidth>
                <PageHeader>Active Spiff Rules</PageHeader>
                {!isEditing && (
                  <Button
                    variant="secondary"
                    onClick={() => setIsEditing(true)}
                    className="2xl:hidden"
                    size="sm"
                  >
                    Edit
                  </Button>
                )}
              </Stack>

              <button
                onClick={() => navigate("/spiffs")}
                className="flex w-fit items-center gap-1 text-sm text-primary underline hover:text-primary/80 2xl:hidden"
              >
                <ArrowLeftIcon className="h-3 w-3" />
                Back to Spiff Payroll
              </button>

              <PageMainCard className="h-[calc(100vh-200px)]">
                <PageMainStack className="pl-6">
                  <ScrollArea className="h-full w-full flex-1 pr-6">
                    {sortedActiveSpiffRules.map(
                      (rule: SpiffRule, index: number) => (
                        <SpiffRuleListItem
                          key={rule.id}
                          spiffRule={rule}
                          isLastItem={
                            index === sortedActiveSpiffRules.length - 1
                          }
                        />
                      ),
                    )}
                  </ScrollArea>
                </PageMainStack>
              </PageMainCard>
            </Stack>
          )}
        </Stack>
      </Stack>
    </div>
  );
};

export default SpiffPrograms;
